import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import LoadingModal from '../modal/LoadingModal';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHook';
import useRateRenewal from '../../hooks/useRateRenewal';
import { IPaymentOptionsValues } from '../../pages/renewal/PaymentOptions';
import { updateCheckout } from '../../store/renewal.slice';
import { getDocument } from '../../utils';
import { addonDocumentEnums } from '../../utils/constants';

const NoClaimsDiscountCard = () => {
	const [loading, setLoading] = useState(false);
	const renewalStore = useAppSelector((redux) => redux.renewal);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const financedPolicy = import.meta.env.VITE_AUTH_REALM !== 'Motorcade';
	const dispatch = useAppDispatch();
	const { rateRenewal } = useRateRenewal();

	const onChange = async (protectNoClaimsBonus: boolean) => {
		setLoading(true);

		try {
			const renewalUpdated = {
				...renewalStore,
				ncbProtection: protectNoClaimsBonus,
			};
			await rateRenewal({
				renewalState: renewalUpdated,
				fullPolicy,
				redirectToSummary: false,
			});
			const updateCheckoutAmount: IPaymentOptionsValues = {
				method: renewalStore.paymentOptions.method,
				deposit: renewalStore.paymentOptions.deposit,
			};
			dispatch(updateCheckout(updateCheckoutAmount));
			setLoading(false);
		} catch (error) {
			console.error('Error updating renewal quote:', error);
			setLoading(false);
			toast('Unable to process quote with no claims protection. Please try again.', {
				position: 'bottom-center',
				autoClose: 3000,
				pauseOnHover: true,
				type: 'error',
			});
		}
	};

	const hasNoClaimsProtection = renewalStore.renewalRatingResponse?.coverDetails?.ncbDetails.isNCBSelected;
	const ncbPriceIncIPT = renewalStore.renewalRatingResponse?.coverDetails?.ncbDetails.amountIncludingIpt;

	const ncbDocument = getDocument(fullPolicy.product, fullPolicy.isShortTermPolicy, addonDocumentEnums.NCB_PROTECTION);

	return (
		<Box
			sx={(theme) => ({
				border: `2px solid ${theme.palette.grey[200]}`,
				borderRadius: 2,
			})}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					my: 1,
					m: 1,
					p: 1,
				}}
			>
				<Box>
					{hasNoClaimsProtection ? (
						<>
							<Typography sx={{ fontWeight: 'bold' }}>
								No Claims Discount Protection - {renewalStore.renewalRatingResponse?.coverDetails?.ncbDetails?.ncbYears}{' '}
								Years
							</Typography>

							<Typography variant='h5' fontWeight='bold'>
								{formatCurrency(ncbPriceIncIPT ?? 0)}
							</Typography>
							<Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
								*Price is calculated annually and includes IPT.
								{financedPolicy ? ' If you choose to pay by Direct Debit, the cost of interest is not shown here.' : ''}
							</Typography>
						</>
					) : (
						<Typography sx={{ fontWeight: 'bold' }}>
							Do you wish to add No Claims Discount Protection? -{' '}
							{renewalStore.renewalRatingResponse?.coverDetails?.ncbDetails?.ncbYears} Years
						</Typography>
					)}
				</Box>
				{hasNoClaimsProtection && <CheckCircleIcon color='primary' fontSize='large' />}
			</Box>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					my: 2,
					m: 1,
					p: 1,
				}}
			>
				{hasNoClaimsProtection ? (
					<Button
						aria-label='remove-protection'
						fullWidth
						variant='contained'
						startIcon={<DeleteIcon />}
						onClick={async () => {
							await onChange(false);
						}}
					>
						Remove Protection
					</Button>
				) : (
					<Button
						aria-label='add-protection'
						fullWidth
						variant='contained'
						onClick={async () => {
							await onChange(true);
						}}
					>
						Add Protection
					</Button>
				)}
			</Box>
			<Accordion
				sx={(theme) => ({
					backgroundColor: 'unset',
					borderRadius: 'unset',
					borderTopLeftRadius: 'unset',
					borderTopRightRadius: 'unset',
					boxShadow: 'unset',
					borderBottom: `1px solid ${theme.palette.grey[300]}`,
					m: 1,

					'&:before': {
						backgroundColor: 'unset',
					},
					'&:last-of-type': {
						borderBottomLeftRadius: 'unset',
						borderBottomRightRadius: 'unset',
					},
				})}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls='tell-me-more-content-ncd'
					id='tell-me-more-header-ncd'
				>
					<Typography>Tell me more</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Typography
						sx={(theme) => ({
							'& a': {
								color: theme.palette.primary.main,
								'&:hover': {
									color: theme.palette.primary.light,
								},
								'&:visited': {
									color: theme.palette.primary.dark,
									'&:hover': {
										color: theme.palette.primary.light,
									},
								},
							},
						})}
					>
						<p>
							No claims discount protection does not protect the overall price of your insurance policy. The price of
							your insurance policy may increase following an accident even if you were not at fault.{' '}
						</p>
						<p>
							No claims discount protection allows you to make one or more claims before your number of no claims
							discount years falls. Please find further details and more information about the step-back procedures on
							the following link:
						</p>
						<p>
							<a href={ncbDocument} target='_blank' rel='noreferrer'>
								No Claims Discount & No Claims Discount Protection
							</a>
						</p>
					</Typography>
				</AccordionDetails>
			</Accordion>
			<LoadingModal open={loading} message='Updating Your Renewal Quote...' />
		</Box>
	);
};
export default NoClaimsDiscountCard;
